import React, { useCallback, useMemo, useState } from "react";
import styled from "styles/styled_components";
import { Maybe } from "true-myth";

import BlockHoverMask from "components/schedule/block_hover_mask";
import {
  StyledTimeBlock,
  StyledRole,
  StyledTime,
  BORDERS,
} from "components/schedule/time_block";

import { OpenBlockFunc } from "actions/scheduling/blocks";
import { formatDateRange, isAllDay } from "helpers/times";
import ellipsis from "styles/ellipsis";
import { IUnavailabilityBlock, Frequency, IPerson } from "types/schedules";
import { trackEvent } from "helpers/analytics";

const StyledUnavailDiv = styled(StyledTimeBlock)`
  background-color: ${({ theme }) => theme.colors.dsGrayLightest};
`;

const StyledUnavailTime = styled(StyledTime)`
  font-family: ${({ theme }) => theme.text.italicFamily};
  ${ellipsis}
`;

interface IUnavailButton {
  border: BORDERS;
  date: Date;
  deleteUnavail: (unavail: IUnavailabilityBlock) => void;
  openBlockModal: OpenBlockFunc;
  person: IPerson;
  unavail: IUnavailabilityBlock;
}

enum UnavailLabels {
  ALL_DAY = "All day",
  TIME_OFF = "Time Off",
  UNAVAIL = "Unavailable",
}

export default function UnavailButton({
  border,
  date,
  deleteUnavail,
  openBlockModal,
  person,
  unavail,
}: IUnavailButton) {
  const { startTime, endTime, frequency } = unavail;
  const isWeekly = frequency === Frequency.WEEKLY;
  const blockType = isWeekly ? "unavailability" : "time off";
  const label = isWeekly ? UnavailLabels.UNAVAIL : UnavailLabels.TIME_OFF;
  const time = isAllDay(startTime, endTime)
    ? UnavailLabels.ALL_DAY
    : formatDateRange(startTime, endTime);

  const [isHovered, setHover] = useState(false);
  const setHoverCurried = useCallback(
    newVal => () => {
      if (newVal) {
        trackEvent("Time Block - Hover", { blockType });
      }
      setHover(newVal);
    },
    [blockType]
  );

  const blockModalData = useMemo(
    () => ({
      existingBlock: Maybe.of(unavail),
      person,
      date,
    }),
    [unavail, person, date]
  );

  const onEdit = useCallback(
    () => {
      trackEvent("Time Block - Edit Icon - Click", { blockType });
      openBlockModal(blockModalData);
    },
    [blockModalData, isWeekly, blockType]
  );
  const onDelete = useCallback(
    () => {
      // Recurring unavails require a confirmation step, open the modal in that flow
      if (isWeekly) {
        openBlockModal({ ...blockModalData, confirmingDelete: true });
      } else {
        // One-off unavails can be deleted immediately
        // TODO: Create a confirmation here as well?
        deleteUnavail(unavail);
      }
      trackEvent("Time Block - Delete Icon - Click", { blockType });
    },
    [blockModalData, isWeekly, unavail, blockType]
  );

  const hoverMask = isHovered ? (
    <BlockHoverMask
      onEdit={isWeekly ? undefined : onEdit}
      onDelete={onDelete}
    />
  ) : (
    undefined
  );

  return (
    <StyledUnavailDiv
      border={border}
      onMouseEnter={setHoverCurried(true)}
      onMouseLeave={setHoverCurried(false)}>
      <StyledUnavailTime>{time}</StyledUnavailTime>
      <StyledRole>{label}</StyledRole>
      {hoverMask}
    </StyledUnavailDiv>
  );
}
